/**
 * Channels Handlers
 */

import { ChannelsState, RetailStoreSettings, createInitialFormData, createInitialDefaultValues, createInitialCategoriesListView, emptyChannelImportIntent } from './state';
import { ChannelsCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { categoriesHandlers } from './categoriesHandlers';

type ExtractCommand<T extends string> = Extract<ChannelsCommand, { type: T }>;

// =============================================================================
// DATA HANDLERS
// =============================================================================

const handleDataUpsert = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/data/upsert'>
): ChannelsState => ({
  ...state,
  salesChannels: {
    ...state.salesChannels,
    [cmd.payload.channel.id]: cmd.payload.channel
  }
});

const handleDataRemove = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/data/remove'>
): ChannelsState => {
  const { [cmd.payload.integrationId]: _, ...rest } = state.salesChannels;
  return { ...state, salesChannels: rest };
};

const handleDataSetAll = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/data/setAll'>
): ChannelsState => ({
  ...state,
  salesChannels: cmd.payload.channels
});

// =============================================================================
// DETAIL VIEW HANDLERS
// =============================================================================

// Navigation
const handleDetailOpen = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/open'>
): ChannelsState => {
  const { integrationId } = cmd.payload;
  // Preserve the current tab when re-opening the same channel so a page-refresh
  // race (channels snapshot arrives after this command fires) doesn't clobber an
  // in-progress tab selection.  Navigating to a different channel always resets.
  const alreadyOpen =
    state.channelDetail.mode.type === 'view' &&
    state.channelDetail.mode.integrationId === integrationId;

  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      mode: { type: 'view', integrationId },
      activeTab: alreadyOpen ? state.channelDetail.activeTab : 'general',
      ...( alreadyOpen ? {} : { categoriesListView: createInitialCategoriesListView() })
    }
  };
};

const handleDetailClose = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    mode: { type: 'closed' },
    activeTab: 'general',
    categories: [],
    categoriesListView: createInitialCategoriesListView(),
    categorySearch: { activeExternalId: null, query: '', results: [], offset: 0, hasMore: false, pendingLoadMore: false },
    pendingCategoryWrites: [],
    pendingCategoryResync: false,
    aiMappingReadyRunId: null,
    billingEntityEdit: {
      isEditing: false,
      tempValue: null
    },
    retailStoreConfigEdit: {
      isEditingWarehouses: false,
      isEditingPriceLists: false,
      tempWarehouseIds: [],
      tempDefaultChannelWarehouseId: null,
      tempPriceListIds: [],
      tempDefaultPriceListId: null
    },
    statusMapping: {
      rows: [],
      canonicalStatuses: [],
      isLoading: false,
      isSaving: false
    },
    defaultValues: createInitialDefaultValues(),
    billingEntities: [],
    availableWarehouses: [],
    availablePriceLists: [],
    loading: {
      billingEntities: false,
      defaultValues: false,
      savingBilling: false,
      savingDefaults: false,
      importing: false,
      loadingWarehouses: false,
      loadingPriceLists: false,
      savingRetailStoreConfig: false,
      categoriesLoading: false,
      categorySearchLoading: false,
      categorySearchLoadingMore: false,
      categoriesResyncing: false
    },
    saveSuccess: false
  }
});

const handleDetailSetTab = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/setTab'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    activeTab: cmd.payload.tab
  }
});

// Billing Entity
const handleBillingEntityStartEdit = ( state: ChannelsState ): ChannelsState => {
  const channel = state.channelDetail.mode.type === 'view' 
    ? state.salesChannels[state.channelDetail.mode.integrationId]
    : null;
  
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      billingEntityEdit: {
        isEditing: true,
        tempValue: channel?.billingEntityId ?? null
      }
    }
  };
};

const handleBillingEntityUpdateTemp = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/billingEntity/updateTemp'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    billingEntityEdit: {
      ...state.channelDetail.billingEntityEdit,
      tempValue: cmd.payload.value
    }
  }
});

const handleBillingEntitySave = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      savingBilling: true
    }
  }
});

const handleBillingEntityCancel = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    billingEntityEdit: {
      isEditing: false,
      tempValue: null
    }
  }
});

// Default Values
const handleDefaultValuesSetConfig = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/defaultValues/setConfig'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    defaultValues: {
      fieldConfig: cmd.payload.fieldConfig,
      data: cmd.payload.data,
      originalData: cmd.payload.data
    }
  }
});

const handleDefaultValuesUpdateField = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/defaultValues/updateField'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    defaultValues: {
      ...state.channelDetail.defaultValues,
      data: {
        ...state.channelDetail.defaultValues.data,
        [cmd.payload.field]: cmd.payload.value
      }
    }
  }
});

const handleDefaultValuesSave = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      savingDefaults: true
    },
    saveSuccess: false
  }
});

const handleDefaultValuesSaveSuccess = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    defaultValues: {
      ...state.channelDetail.defaultValues,
      originalData: state.channelDetail.defaultValues.data
    },
    saveSuccess: true
  }
});

const handleDefaultValuesClearSuccess = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    saveSuccess: false
  }
});

// External Data
const handleBillingEntitiesSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/billingEntities/set'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    billingEntities: cmd.payload.entities
  }
});

// Status Mapping (WS-3)
const handleStatusMappingLoad = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/statusMapping/load'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      rows: cmd.payload.rows,
      canonicalStatuses: cmd.payload.canonicalStatuses,
      isLoading: false
    }
  }
});

const handleStatusMappingAddRow = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      rows: [ ...state.channelDetail.statusMapping.rows, { channelStatus: '', canonicalStatusId: '' }]
    }
  }
});

const handleStatusMappingUpdateRow = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/statusMapping/updateRow'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      rows: state.channelDetail.statusMapping.rows.map(( row, index ) =>
        index === cmd.payload.index
          ? {
            channelStatus: cmd.payload.channelStatus ?? row.channelStatus,
            canonicalStatusId: cmd.payload.canonicalStatusId ?? row.canonicalStatusId
          }
          : row )
    }
  }
});

const handleStatusMappingRemoveRow = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/statusMapping/removeRow'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      rows: state.channelDetail.statusMapping.rows.filter(( _, index ) => index !== cmd.payload.index )
    }
  }
});

// Trigger for the save policy
const handleStatusMappingSave = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      isSaving: true
    }
  }
});

const handleSetStatusMappingLoading = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setStatusMapping'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      isLoading: cmd.payload.isLoading
    }
  }
});

const handleSetSavingStatusMapping = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setSavingStatusMapping'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    statusMapping: {
      ...state.channelDetail.statusMapping,
      isSaving: cmd.payload.isLoading
    }
  }
});

// Actions (triggers for policies)
// TV1-2354 — set-semantics: ticking the box twice for the same connector must
// not queue two imports.
/*
 * TV1-2361 — MERGES rather than replaces, so the two loaders (categories in one
 * trip, publication counts per channel) can land independently without the
 * second wiping the first.
 */
const handleStatsSet = (
  state: ChannelsState,
  cmd: Extract<ChannelsCommand, { type: 'integrations/channels/stats/set' }>
): ChannelsState => {
  const merged = { ...state.channelStats };
  for ( const [ id, incoming ] of Object.entries( cmd.payload.stats )) {
    const current = merged[id];
    /*
     * THREE independent loaders dispatch into this slice and they race, so the
     * merge must be field-by-field with "this loader had no opinion" (undefined)
     * distinguished from a real value.
     *
     * It is an EXPLICIT field list rather than a spread because two spreads over
     * partials produced a TS2783 duplicate-key error — but an explicit list has a
     * failure mode of its own, and it bit: `setup` was added to the state type and
     * to the policy that loads it, and NOT here, so every dispatch from the other
     * two loaders silently rebuilt the entry without it. The field never survived
     * long enough to reach a card, and the symptom was a blank status row rather
     * than an error. Anything added to `channelStats` must be added here too.
     */
    merged[id] = {
      mappedCategories: incoming.mappedCategories ?? current?.mappedCategories ?? 0,
      totalCategories: incoming.totalCategories ?? current?.totalCategories ?? 0,
      // `undefined` means "this loader had no opinion"; `null` is a real value
      // meaning "the count query failed for this channel".
      publications: incoming.publications !== undefined
        ? incoming.publications
        : ( current?.publications ?? null ),
      // Left `undefined` until the setup loader speaks — the card renders no
      // verdict at all in that state, deliberately (see `deriveChannelCardState`).
      setup: incoming.setup !== undefined ? incoming.setup : current?.setup
    };
  }
  return { ...state, channelStats: merged };
};

/**
 * Arm ONE import kind for a connector, leaving the other kind exactly as it was
 * (TV1-2379). Idempotent: re-arming a kind that is already armed returns the
 * SAME state object, so a re-dispatch cannot wake `channelAutoImportPolicy`'s
 * `shouldReact` or manufacture a second import.
 */
const handleAutoImportSetPending = (
  state: ChannelsState,
  cmd: Extract<ChannelsCommand, { type: 'integrations/channels/autoImport/setPending' }>
): ChannelsState => {
  const { connectorId, kind } = cmd.payload;
  const current = state.pendingChannelImports[connectorId] ?? emptyChannelImportIntent();
  if ( current[kind]) return state;
  return {
    ...state,
    pendingChannelImports: {
      ...state.pendingChannelImports,
      [connectorId]: { ...current, [kind]: true }
    }
  };
};

/**
 * Disarm an intent. With `kind`, only that one kind — the other survives. Without
 * `kind`, the connector's whole entry goes, both kinds at once: that is the
 * consume-on-arrival path, and clearing wholesale is what keeps the intent
 * one-shot (see `channelAutoImportPolicy`).
 *
 * An entry with nothing left armed is DELETED rather than kept as all-false, so
 * `Object.keys( pendingChannelImports ).length === 0` stays an honest "nothing
 * is pending" for the policy's cheap `shouldReact` guard.
 */
const handleAutoImportClearPending = (
  state: ChannelsState,
  cmd: Extract<ChannelsCommand, { type: 'integrations/channels/autoImport/clearPending' }>
): ChannelsState => {
  const { connectorId, kind } = cmd.payload;
  const current = state.pendingChannelImports[connectorId];
  if ( !current ) return state;

  const remaining = kind ? { ...current, [kind]: false } : emptyChannelImportIntent();
  const pendingChannelImports = { ...state.pendingChannelImports };
  if ( remaining.publications || remaining.orders ) {
    pendingChannelImports[connectorId] = remaining;
  } else {
    delete pendingChannelImports[connectorId];
  }
  return { ...state, pendingChannelImports };
};

const handleImportPublications = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      importing: true
    }
  }
});

const handleDetailDelete = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  loading: { ...state.loading, deleting: true }
});

// Loading States
const handleSetBillingEntitiesLoading = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setBillingEntities'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      billingEntities: cmd.payload.isLoading
    }
  }
});

const handleSetDefaultValuesLoading = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setDefaultValues'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      defaultValues: cmd.payload.isLoading
    }
  }
});

const handleSetSavingBilling = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setSavingBilling'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      savingBilling: cmd.payload.isLoading
    }
  }
});

const handleSetSavingDefaults = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setSavingDefaults'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      savingDefaults: cmd.payload.isLoading
    }
  }
});

const handleSetImporting = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setImporting'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      importing: cmd.payload.isLoading
    }
  }
});

// =============================================================================
// RETAIL STORE CONFIG HANDLERS
// =============================================================================

const handleRetailStoreConfigStartEdit = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/retailStoreConfig/startEdit'>
): ChannelsState => {
  const channel = state.channelDetail.mode.type === 'view'
    ? state.salesChannels[state.channelDetail.mode.integrationId]
    : null;

  const settings = ( channel?.settings ?? {}) as Partial<RetailStoreSettings>;
  const { section } = cmd.payload;

  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      retailStoreConfigEdit: {
        ...state.channelDetail.retailStoreConfigEdit,
        ...( section === 'warehouses' ? {
          isEditingWarehouses: true,
          tempWarehouseIds: settings.warehouses ?? [],
          tempDefaultChannelWarehouseId: settings.defaultChannelWarehouseId ?? null
        } : {
          isEditingPriceLists: true,
          tempPriceListIds: settings.priceLists ?? [],
          tempDefaultPriceListId: settings.defaultPriceListId ?? null
        })
      }
    }
  };
};

const handleRetailStoreConfigUpdateWarehouses = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/retailStoreConfig/updateWarehouses'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    retailStoreConfigEdit: {
      ...state.channelDetail.retailStoreConfigEdit,
      tempWarehouseIds: cmd.payload.selectedIds,
      tempDefaultChannelWarehouseId: cmd.payload.defaultId
    }
  }
});

const handleRetailStoreConfigUpdatePriceLists = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/retailStoreConfig/updatePriceLists'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    retailStoreConfigEdit: {
      ...state.channelDetail.retailStoreConfigEdit,
      tempPriceListIds: cmd.payload.selectedIds,
      tempDefaultPriceListId: cmd.payload.defaultId
    }
  }
});

const handleRetailStoreConfigSave = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      savingRetailStoreConfig: true
    }
  }
});

const handleRetailStoreConfigCancel = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/retailStoreConfig/cancel'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    retailStoreConfigEdit: {
      ...state.channelDetail.retailStoreConfigEdit,
      ...( cmd.payload.section === 'warehouses' ? {
        isEditingWarehouses: false,
        tempWarehouseIds: [],
        tempDefaultChannelWarehouseId: null
      } : {
        isEditingPriceLists: false,
        tempPriceListIds: [],
        tempDefaultPriceListId: null
      })
    }
  }
});

const handleWarehousesSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/warehouses/set'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    availableWarehouses: cmd.payload.warehouses
  }
});

const handlePriceListsSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/priceLists/set'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    availablePriceLists: cmd.payload.priceLists
  }
});

const handleSetLoadingWarehouses = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setLoadingWarehouses'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      loadingWarehouses: cmd.payload.isLoading
    }
  }
});

const handleSetLoadingPriceLists = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setLoadingPriceLists'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      loadingPriceLists: cmd.payload.isLoading
    }
  }
});

const handleSetSavingRetailStoreConfig = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/detail/loading/setSavingRetailStoreConfig'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      savingRetailStoreConfig: cmd.payload.isLoading
    }
  }
});

// =============================================================================
// ADD MODAL HANDLERS
// =============================================================================

const handleAddModalOpen = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  addChannelModal: {
    isOpen: true,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  }
});

const handleAddModalClose = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  addChannelModal: {
    ...state.addChannelModal,
    isOpen: false,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  }
});

const handleAddModalSelectType = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/addModal/selectType'>
): ChannelsState => ({
  ...state,
  addChannelModal: {
    ...state.addChannelModal,
    selectedType: cmd.payload.channelType,
    step: 'enterDetails'
  }
});

const handleAddModalUpdateField = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/addModal/updateField'>
): ChannelsState => {
  // Remove the error for the updated field
  const { [cmd.payload.field]: _, ...remainingErrors } = state.addChannelModal.formErrors;
  return {
    ...state,
    addChannelModal: {
      ...state.addChannelModal,
      formData: {
        ...state.addChannelModal.formData,
        [cmd.payload.field]: cmd.payload.value
      },
      formErrors: remainingErrors
    }
  };
};

const handleAddModalSetStep = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/addModal/setStep'>
): ChannelsState => ({
  ...state,
  addChannelModal: {
    ...state.addChannelModal,
    step: cmd.payload.step
  }
});

const handleAddModalSubmit = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  addChannelModal: {
    ...state.addChannelModal,
    step: 'connecting'
  }
});

const handleAddModalReset = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  addChannelModal: {
    isOpen: state.addChannelModal.isOpen,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  }
});

// =============================================================================
// DELETE MODAL HANDLERS
// =============================================================================

const handleDeleteOpen = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/delete/open'>
): ChannelsState => ({
  ...state,
  deleteModal: {
    isOpen: true,
    integrationId: cmd.payload.integrationId
  }
});

const handleDeleteClose = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  deleteModal: {
    isOpen: false,
    integrationId: null
  }
});

const handleDeleteConfirm = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  loading: { ...state.loading, deleting: true }
});

// =============================================================================
// OAUTH HANDLERS
// =============================================================================

const handleOAuthStart = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/oauth/start'>
): ChannelsState => ({
  ...state,
  oauthRedirect: {
    isProcessing: true,
    code: cmd.payload.code,
    state: cmd.payload.state
  }
});

const handleOAuthComplete = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  oauthRedirect: {
    isProcessing: false,
    code: null,
    state: null
  }
});

const handleOAuthFail = ( state: ChannelsState ): ChannelsState => ({
  ...state,
  oauthRedirect: {
    isProcessing: false,
    code: null,
    state: null
  }
});

// =============================================================================
// GLOBAL LOADING HANDLERS
// =============================================================================

const handleSetChannelsLoading = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/loading/setChannels'>
): ChannelsState => ({
  ...state,
  loading: { ...state.loading, channelsLoading: cmd.payload.isLoading }
});

const handleSetSubmitting = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/loading/setSubmitting'>
): ChannelsState => ({
  ...state,
  loading: { ...state.loading, submitting: cmd.payload.isLoading }
});

const handleSetDeleting = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/loading/setDeleting'>
): ChannelsState => ({
  ...state,
  loading: { ...state.loading, deleting: cmd.payload.isLoading }
});

const handleSetImportingGlobal = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/loading/setImporting'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      importing: cmd.payload.isLoading
    }
  }
});

// ============================================================================
// HANDLER MAP
// =============================================================================

export const handlers: CommandHandlerMap<ChannelsState, ChannelsCommand> = {
  // Data
  'integrations/channels/data/upsert': handleDataUpsert,
  'integrations/channels/data/remove': handleDataRemove,
  'integrations/channels/data/setAll': handleDataSetAll,
  
  // Detail - Navigation
  'integrations/channels/detail/open': handleDetailOpen,
  'integrations/channels/detail/close': handleDetailClose,
  'integrations/channels/detail/setTab': handleDetailSetTab,
  
  // Detail - Billing Entity
  'integrations/channels/detail/billingEntity/startEdit': handleBillingEntityStartEdit,
  'integrations/channels/detail/billingEntity/updateTemp': handleBillingEntityUpdateTemp,
  'integrations/channels/detail/billingEntity/save': handleBillingEntitySave,
  'integrations/channels/detail/billingEntity/cancel': handleBillingEntityCancel,
  
  // Detail - Default Values
  'integrations/channels/detail/defaultValues/setConfig': handleDefaultValuesSetConfig,
  'integrations/channels/detail/defaultValues/updateField': handleDefaultValuesUpdateField,
  'integrations/channels/detail/defaultValues/save': handleDefaultValuesSave,
  'integrations/channels/detail/defaultValues/saveSuccess': handleDefaultValuesSaveSuccess,
  'integrations/channels/detail/defaultValues/clearSuccess': handleDefaultValuesClearSuccess,
  
  // Detail - Status Mapping
  'integrations/channels/detail/statusMapping/load': handleStatusMappingLoad,
  'integrations/channels/detail/statusMapping/addRow': handleStatusMappingAddRow,
  'integrations/channels/detail/statusMapping/updateRow': handleStatusMappingUpdateRow,
  'integrations/channels/detail/statusMapping/removeRow': handleStatusMappingRemoveRow,
  'integrations/channels/detail/statusMapping/save': handleStatusMappingSave,

  // Detail - External Data
  'integrations/channels/detail/billingEntities/set': handleBillingEntitiesSet,
  
  // Detail - Actions
  'integrations/channels/detail/importPublications': handleImportPublications,
  'integrations/channels/stats/set': handleStatsSet,
  'integrations/channels/autoImport/setPending': handleAutoImportSetPending,
  'integrations/channels/autoImport/clearPending': handleAutoImportClearPending,
  'integrations/channels/detail/delete': handleDetailDelete,
  
  // Detail - RetailStore Config
  'integrations/channels/detail/retailStoreConfig/startEdit': handleRetailStoreConfigStartEdit,
  'integrations/channels/detail/retailStoreConfig/updateWarehouses': handleRetailStoreConfigUpdateWarehouses,
  'integrations/channels/detail/retailStoreConfig/updatePriceLists': handleRetailStoreConfigUpdatePriceLists,
  'integrations/channels/detail/retailStoreConfig/save': handleRetailStoreConfigSave,
  'integrations/channels/detail/retailStoreConfig/cancel': handleRetailStoreConfigCancel,

  // Detail - Reference Data
  'integrations/channels/detail/warehouses/set': handleWarehousesSet,
  'integrations/channels/detail/priceLists/set': handlePriceListsSet,

  // Detail - Loading States
  'integrations/channels/detail/loading/setBillingEntities': handleSetBillingEntitiesLoading,
  'integrations/channels/detail/loading/setDefaultValues': handleSetDefaultValuesLoading,
  'integrations/channels/detail/loading/setSavingBilling': handleSetSavingBilling,
  'integrations/channels/detail/loading/setSavingDefaults': handleSetSavingDefaults,
  'integrations/channels/detail/loading/setImporting': handleSetImporting,
  'integrations/channels/detail/loading/setLoadingWarehouses': handleSetLoadingWarehouses,
  'integrations/channels/detail/loading/setLoadingPriceLists': handleSetLoadingPriceLists,
  'integrations/channels/detail/loading/setSavingRetailStoreConfig': handleSetSavingRetailStoreConfig,
  'integrations/channels/detail/loading/setStatusMapping': handleSetStatusMappingLoading,
  'integrations/channels/detail/loading/setSavingStatusMapping': handleSetSavingStatusMapping,

  // Category Mapping (extracted to categoriesHandlers.ts)
  ...categoriesHandlers,

  // Add Modal
  'integrations/channels/addModal/open': handleAddModalOpen,
  'integrations/channels/addModal/close': handleAddModalClose,
  'integrations/channels/addModal/selectType': handleAddModalSelectType,
  'integrations/channels/addModal/updateField': handleAddModalUpdateField,
  'integrations/channels/addModal/setStep': handleAddModalSetStep,
  'integrations/channels/addModal/submit': handleAddModalSubmit,
  'integrations/channels/addModal/reset': handleAddModalReset,
  
  // Delete
  'integrations/channels/delete/open': handleDeleteOpen,
  'integrations/channels/delete/close': handleDeleteClose,
  'integrations/channels/delete/confirm': handleDeleteConfirm,
  
  // OAuth
  'integrations/channels/oauth/start': handleOAuthStart,
  'integrations/channels/oauth/complete': handleOAuthComplete,
  'integrations/channels/oauth/fail': handleOAuthFail,
  
  // Global Loading
  'integrations/channels/loading/setChannels': handleSetChannelsLoading,
  'integrations/channels/loading/setSubmitting': handleSetSubmitting,
  'integrations/channels/loading/setDeleting': handleSetDeleting,
  'integrations/channels/loading/setImporting': handleSetImportingGlobal
};
